/**
 * LeadKanbanCard
 * Cartão de lead no quadro (arrastável enquanto o lead está ativo).
 */

import React from 'react';
import { Building2, CalendarClock, GripVertical, Mail, MessageCircle, Phone } from 'lucide-react';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/utils/cn';
import type { Lead } from '../../types/leads';
import { LEAD_PRIORITY_LABELS, LEAD_SOURCE_LABELS } from '../../types/leads';
import { useLeadStatusTransition } from '../../hooks/useLeadStatusTransition';
import { formatLeadValue, leadCreatedAgo, leadWhatsAppUrl } from './lead-format';

interface LeadKanbanCardProps {
  lead: Lead;
  onClick: () => void;
}

export const LeadKanbanCard: React.FC<LeadKanbanCardProps> = ({ lead, onClick }) => {
  const { canDragLead } = useLeadStatusTransition();
  const isDraggable = canDragLead(lead);

  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: lead.id,
    disabled: !isDraggable,
    data: { type: 'lead', lead }
  });

  const style = { transform: CSS.Transform.toString(transform) };
  const whatsappUrl = leadWhatsAppUrl(lead);
  const value = formatLeadValue(lead.estimatedValue);

  const openWhatsApp = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (whatsappUrl) window.open(whatsappUrl, '_blank');
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={onClick}
      className={cn(
        'relative cursor-pointer rounded-md border bg-card p-3 text-sm shadow-xs transition-shadow',
        isDragging ? 'opacity-50 ring-2 ring-ring' : 'hover:shadow-md',
        !isDraggable && 'opacity-75'
      )}
    >
      {isDraggable && (
        <button
          type="button"
          {...listeners}
          {...attributes}
          aria-label={`Arrastar ${lead.name}`}
          className="absolute top-2 right-2 cursor-grab rounded-sm p-1 text-muted-foreground hover:bg-muted active:cursor-grabbing"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <GripVertical className="size-4" />
        </button>
      )}

      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        className="block w-full pr-6 text-left"
      >
        <span className="line-clamp-1 font-medium hover:underline">{lead.name}</span>
        {lead.companyName && (
          <span className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
            <Building2 className="size-3" /> {lead.companyName}
          </span>
        )}
      </button>

      <div className="mt-2 flex flex-wrap gap-1">
        {lead.isHotLead && <Badge variant="destructive">Quente</Badge>}
        {lead.isQualified && <Badge variant="secondary">Qualificado</Badge>}
        <Badge variant="outline">Prioridade {(LEAD_PRIORITY_LABELS[lead.priority] ?? lead.priority).toLowerCase()}</Badge>
        {lead.legalArea && <Badge variant="outline">{lead.legalArea}</Badge>}
      </div>

      <div className="mt-2 flex flex-col gap-0.5 text-xs text-muted-foreground">
        {lead.email && (
          <span className="flex items-center gap-1 truncate">
            <Mail className="size-3 shrink-0" /> {lead.email}
          </span>
        )}
        {lead.whatsapp && (
          <span className="flex items-center justify-between gap-1">
            <span className="flex items-center gap-1"><Phone className="size-3 shrink-0" /> {lead.whatsapp}</span>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon-xs" onClick={openWhatsApp} aria-label="Abrir WhatsApp">
                  <MessageCircle />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Abrir WhatsApp</TooltipContent>
            </Tooltip>
          </span>
        )}
      </div>

      {value && <p className="mt-2 font-medium tabular-nums">{value}</p>}

      <div className="mt-2">
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Score</span>
          <span className="font-medium tabular-nums">{lead.score}/100</span>
        </div>
        <div className="mt-1 h-1.5 w-full rounded-full bg-muted" aria-hidden="true">
          <div className="h-1.5 rounded-full bg-primary" style={{ width: `${Math.min(Math.max(lead.score, 0), 100)}%` }} />
        </div>
      </div>

      <div className="mt-2 flex items-center justify-between border-t pt-2 text-xs text-muted-foreground">
        <span>{LEAD_SOURCE_LABELS[lead.source]}</span>
        <span>{leadCreatedAgo(lead)}</span>
      </div>

      {lead.nextFollowUpDate && (
        <p className="mt-1 flex items-center gap-1 text-xs font-medium">
          <CalendarClock className="size-3 text-muted-foreground" />
          Follow-up: {new Date(lead.nextFollowUpDate).toLocaleDateString('pt-BR')}
        </p>
      )}
    </div>
  );
};

export default LeadKanbanCard;
